import * as fs from 'fs'
import { customComponentSupportLibraryVersion } from './version'

export interface CCCJsonEntry {
  entryPoint: string
  outputPath: string
  name: string
  label: string
  description: string
}

/** Read the custom component collection config from the package.json. Throws error if it can't find it */
export const readCustomComponentLibraryConfig = (packageJson: {
  retoolCustomComponentLibraryConfig?: CCCJsonEntry
}): CCCJsonEntry => {
  if (!packageJson.retoolCustomComponentLibraryConfig) {
    throw new Error('Missing retoolCustomComponentLibraryConfig in package.json')
  }

  return packageJson.retoolCustomComponentLibraryConfig
}

export const getCustomComponentLibraryConfig = (workingDirectory?: string): CCCJsonEntry => {
  const packageJson = JSON.parse(fs.readFileSync(`${workingDirectory ?? '.'}/package.json`).toString())
  return readCustomComponentLibraryConfig(packageJson)
}

export const updateCustomComponentLibraryConfig = (config: CCCJsonEntry) => {
  const packageJson = JSON.parse(fs.readFileSync(`package.json`).toString())
  packageJson.retoolCustomComponentLibraryConfig = config
  fs.writeFileSync(`package.json`, JSON.stringify(packageJson, null, 2))
}

const parseVersion = (version: string): number[] => {
  return version.split('.').map(Number)
}

const compareVersions = (a: string, b: string): number => {
  const versionA = parseVersion(a)
  const versionB = parseVersion(b)

  // We only compare major + minor versions - we don't notify users about patch bumps
  // This allows us to use patch to make minor changes we don't want to bother users about.
  for (let i = 0; i < 2; i++) {
    if (versionA[i] < versionB[i]) return -1
    if (versionA[i] > versionB[i]) return 1
  }

  return 0
}

const colors = {
  reset: '\x1b[0m',
  red: '\x1b[31m',
}

export const checkIfNewerVersionAvailableAndLog = async (skip: boolean) => {
  if (!skip) {
    try {
      const endpoint = 'https://registry.npmjs.org/@tryretool/custom-component-support'
      const res = await fetch(endpoint)
      const data = await res.json()
      const versions: string[] = Object.keys(data['versions'])
      versions.sort(compareVersions)

      if (compareVersions(customComponentSupportLibraryVersion, versions[versions.length - 1]) < 0) {
        console.log(
          `${colors.red}\n=============\n` +
          'Newer version of @tryretool/custom-component-support available!\n' +
          'Please upgrade using the appropriate npm/yarn commands to receive new features and bug fixes:\n' +
          '  yarn upgrade @tryretool/custom-component-support\n' +
          '  npm update @tryretool/custom-component-support\n' +
          '\n' +
          'See https://docs.retool.com/apps/web/guides/components/custom-components-beta for more details.\n' +
          `=============${colors.reset}`,
        )
      }
    } catch (error) {
      console.error(
        '[INFO]: Tried to check for new versions of @tryretool/custom-component-support on https://www.npmjs.com ' +
        'but failed. Visit https://www.npmjs.com/package/@tryretool/custom-component-support to see if new versions ' +
        'exist. This error is safe to ignore and does not indicate this command failed. To supress this warning use the --skip-updates-check flag when running commands.',
        {
          error: error.message,
        }
      )
    }
  } else {
    console.log('Skipping updates check.')
  }
}
